import { useState } from "react"
import { busyWhile } from "lib/@components/busy/busy-while"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { generate } from "library/guid"
import { isEnabled } from "lib/@components/feature"
import { setSkillMapTrade } from "routes/skill-mapper/controller/set-skill-map-trade"
import { removeSkillMapTrade } from "routes/skill-mapper/controller/remove-skill-map-trade"
import { setSkillMapLines } from "routes/skill-mapper/controller/set-skill-map-lines"
import { setSkillMapLine } from "routes/skill-mapper/controller/set-skill-map-line"
import { deleteSkillMapLines } from "routes/skill-mapper/controller/delete-skill-map-lines"
import { EditMappingDrawer } from "./edit-mapping-drawer"
import { FEATURE_FLAG_SCHEDULE_ASSIGNMENTS } from "../../feature-flag-schedule-assignments"

export function useEditMapping({ skillMapId, allLines, tradeVocabulary }) {
    const [mapping, setMapping] = useState(null)

    return {
        openEditMapping,
        editMappingDrawer: (
            <EditMappingDrawer
                open={!!mapping}
                mapping={mapping}
                allLines={allLines}
                tradeVocabulary={tradeVocabulary}
                onClose={closeEditMapping}
                onSubmit={handleSubmit}
            />
        ),
    }

    function openEditMapping(nextMapping = {}) {
        setMapping(nextMapping)
    }

    function closeEditMapping() {
        setMapping(null)
    }

    async function handleSubmit(result) {
        const { tradeId, tradeName, skills } = mapping || {}
        const isNew = !tradeId
        if (!result) return

        await busyWhile(
            () => saveMappingChanges({ skillMapId, tradeId, result, initialSkills: skills }),
            "Saving mapping..."
        )
        if (isNew) {
            successSnackbar(`Trade "${result.tradeName.trim()}" added`)
        } else if (hasChanges({ tradeName, skills, result })) {
            successSnackbar(`Trade "${result.tradeName.trim()}" updated`)
        }
        closeEditMapping()
    }
}

async function saveMappingChanges({ skillMapId, tradeId, result, initialSkills }) {
    const isNew = !tradeId
    const finalTradeId = isNew ? generate() : tradeId

    // If the last skills were taken off, remove the trade entirely, unless trades may exist
    // without skills (FIQ-2457): then deleting the trade is a separate, explicit action. A
    // trade that had no skills to begin with is only being renamed
    if (!result.skills.length && !isEnabled(FEATURE_FLAG_SCHEDULE_ASSIGNMENTS) && initialSkills?.length) {
        if (!isNew && initialSkills) {
            const ids = collectLineIds(initialSkills)
            if (ids.length) {
                await deleteSkillMapLines({ ids })
            }
        }
        if (!isNew) {
            await removeSkillMapTrade({ skillMapId, tradeId: finalTradeId })
        }
        return
    }

    // Single-trade upsert: never send the whole trades dictionary from the client,
    // a stale cached read of it would silently drop trades added moments earlier
    await setSkillMapTrade({ skillMapId, tradeId: finalTradeId, name: result.tradeName.trim() })

    const initialIds = new Set((initialSkills || []).map(toSkillId))

    if (isNew) {
        // Add mode: create all lines
        const lines = result.skills.map(toLineInput)
        if (lines.length) {
            await setSkillMapLines({ skillMapId, lines })
        }
    } else {
        // Edit mode: diff skills
        const resultIds = new Set(result.skills.map(toSkillId))

        // Remove skills no longer present
        const removeIds = collectRemovedLineIds(initialSkills, resultIds)
        if (removeIds.length) {
            await deleteSkillMapLines({ ids: removeIds })
        }

        // Add new skills
        const newLines = result.skills.filter(isNewSkill).map(toLineInput)
        if (newLines.length) {
            await setSkillMapLines({ skillMapId, lines: newLines })
        }

        const retainedLines = result.skills.filter(isRetainedSkill).map(toLineInput)
        for (const line of retainedLines) {
            await setSkillMapLine({ skillMapId, line })
        }
    }

    function toLineInput(skill) {
        const line = { skillId: skill.skillId, tradeId: skill.tradeId || finalTradeId }
        if (skill.conditionalTrades !== undefined) line.conditionalTrades = skill.conditionalTrades || []
        return line
    }

    function isNewSkill(s) {
        return !initialIds.has(s.skillId)
    }

    function isRetainedSkill(s) {
        return initialIds.has(s.skillId)
    }
}

function hasChanges({ tradeName, skills, result }) {
    if (result.tradeName.trim() !== (tradeName || "").trim()) return true
    const initialIds = new Set((skills || []).map(toSkillId))
    const resultIds = new Set(result.skills.map(toSkillId))
    if (initialIds.size !== resultIds.size) return true
    for (const id of initialIds) {
        if (!resultIds.has(id)) return true
    }
    if (JSON.stringify(toRoutingComparable(skills)) !== JSON.stringify(toRoutingComparable(result.skills))) return true
    return false
}

function toRoutingComparable(skills) {
    return (skills || []).map((skill) => ({
        skillId: skill.skillId,
        tradeId: skill.tradeId || null,
        conditionalTrades: skill.conditionalTrades || [],
    }))
}

function collectRemovedLineIds(initialSkills, resultIds) {
    const ids = []
    for (const skill of initialSkills || []) {
        if (resultIds.has(skill.skillId)) continue
        ids.push(...getLineIds(skill))
    }
    return ids
}

function collectLineIds(skills) {
    const ids = []
    for (const skill of skills || []) {
        ids.push(...getLineIds(skill))
    }
    return ids
}

function getLineIds(skill) {
    if (Array.isArray(skill.lineIds) && skill.lineIds.length) return skill.lineIds.filter(Boolean)
    if (skill.id) return [skill.id]
    return []
}

function toSkillId(skill) {
    return skill.skillId
}
